<template>
  <div
    class="relative flex max-w-[300px] flex-col gap-1 border border-gray-100 p-4"
    :style="{ width: `${width}px` }"
  >
    <div>
      <div
        v-for="month in months_"
        :key="month.key"
        :class="{
          hidden: currentMonth_.key !== month.key,
        }"
        role="presentation"
      >
        <div id="escv-calendar__header" class="flex w-full flex-col gap-1">
          <div class="relative pb-2 z-20 flex flex-nowrap justify-between">
            <button
              type="button"
              class="text-gray-400 hover:text-gray-600"
              @click="previousMonth"
              style="z-index: 25"
            >
              <AppSvgSymbol name="@escavador/arrow-left" size="16" />
            </button>


            <div class="text-center">
              {{ month.name }}
              <select
                v-if="showSelectYear"
                v-model="month.year"
                class="field px-3 rounded-md text-sm select-year"
                @change="monthInput"
              >
                <option
                  v-for="year in years_"
                  :key="year"
                  :value="year">{{ year }}
                </option>
              </select>
              <span v-else>{{ month.year }}</span>
            </div>


            <button
              type="button"
              class="text-gray-400 hover:text-gray-500"
              @click="nextMonth"
            >
              <AppSvgSymbol name="@escavador/arrow-right" size="16" />
            </button>
          </div>
          <div class="grid grid-cols-7">
            <div
              v-for="name in daysNames"
              :key="name"
              class="px-[2px] text-center text-xs"
            >
              {{ name }}
            </div>
          </div>
        </div>

        <div
          class="grid grid-flow-row gap-[1px] border border-gray-100 bg-gray-100"
        >
          <div
            v-for="(week, i) in month.weeks"
            :key="i"
            class="grid grid-cols-7 gap-[1px]"
          >
            <button
              v-for="day in week"
              :key="day.date"
              type="button"
              class="bg-white text-center hover:bg-gray-100"
              :class="{
                'hover:!bg-gray-100':
                  isGreaterThanFinalDate(day) || isLessThanInitialDate(day),
                '!bg-gray-100':
                  isGreaterThanFinalDate(day) || isLessThanInitialDate(day),
                '!text-brand-blue-800': isToday(day),
                '!font-bold': isToday(day),
                'bg-brand-blue-200': isBreakPointDate(day),
                'bg-brand-blue-100': isMiddleDate(day),
                '-mt-[1px]': day.day === 0 && day.type === 'left',
                '-ml-[1px]': day.day === 0 && day.type === 'left',
                '-mr-[1px]': day.day === 0 && day.type === 'right',
                '-mb-[1px]': day.day === 0 && day.type === 'right',
                'pointer-events-none': day.day === 0,
              }"
              :disabled="
                isGreaterThanFinalDate(day) || isLessThanInitialDate(day)
              "
              :style="{ height: (width - 30) / 7 + 'px' }"
              @click="selectDate(day)"
            >
              {{ day.label || "" }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="flex gap-1" v-if="useControls">
      <AppButton
        type="button"
        class="w-full !py-2 !px-4 hover:bg-gray-100"
        @click="resetDates"
      >
        {{ negativeControl.text }}
      </AppButton>
      <AppButton
        type="button"
        class="w-full !py-2 !px-4 bg-sky-600 text-white hover:bg-sky-700"
        :disabled="!dateOne && !dateTwo"
        @click="dateConfirmation"
      >
        {{ positiveControl.text }}
      </AppButton>
    </div>
  </div>
</template>
<script>
import {
  format,
  getMonth,
  getDaysInMonth,
  startOfMonth,
  subMonths,
  addMonths,
  isAfter,
  isBefore,
  isSameDay,
  setYear,
  subYears
} from "date-fns";

const POSITIVE_CONTROL = { text: "Finalizar" };
const NEGATIVE_CONTROL = { text: "Limpar" };

export default {
  name: "AppCalendar",
  props: {
    width: {
      type: [Number],
      default: 300,
    },
    daysNames: {
      type: Array,
      default: () => ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"],
    },
    monthNames: {
      type: Array,
      default: () => [
        "Janeiro",
        "Fevereiro",
        "Março",
        "Abril",
        "Maio",
        "Junho",
        "Julho",
        "Agosto",
        "Setembro",
        "Outubro",
        "Novembro",
        "Dezembro",
      ],
    },
    initialDate: {
      type: [Date, null],
      default: null,
    },
    finalDate: {
      type: [Date, null],
      default: null,
    },
    selectedDateOne: {
      type: [Date, null],
      default: null,
    },
    selectedDateTwo: {
      type: [Date, null],
      default: null,
    },
    mode: {
      type: String,
      default: "range",
      validator: (value) => {
        return ["range", "single"].includes(value);
      },
    },
    useControls: {
      type: Boolean,
      default: true,
    },
    positiveControl: {
      type: Object,
      default: () => POSITIVE_CONTROL,
    },
    negativeControl: {
      type: Object,
      default: () => NEGATIVE_CONTROL,
    },
    showSelectYear: {
      type: Boolean,
      default: false,
    },
    diffStartYear: {
      type: Number,
      default: 20,
    },
  },
  emits: ["select-date", "start-date", "end-date", "date-confirmation"],
  data() {
    return {
      dateOne: null,
      dateTwo: null,
      months: [],
      currentDate: new Date(),
    };
  },
  computed: {
    today() {
      return new Date();
    },
    currentMonth_() {
      const month = startOfMonth(this.currentDate);
      return this.mountMonth(month);
    },
    months_() {
      const month = startOfMonth(this.currentDate);
      const months = [
        subMonths(month, 2),
        subMonths(month, 1),
        month,
        addMonths(month, 1),
        addMonths(month, 2),
      ];
      return months.map((month) => this.mountMonth(month));
    },
    years_() {
      const endYear = this.today.getFullYear();
      const startYear = subYears(this.today, this.diffStartYear).getFullYear();

      return Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);
    }
  },
  beforeMount() {
    this.currentDate = this.initialDate || this.today;

    this.dateOne = this.selectedDateOne
      ? this.createDayFromDate(this.selectedDateOne)
      : null;

    this.dateTwo = this.selectedDateTwo
      ? this.createDayFromDate(this.selectedDateTwo)
      : null;
  },
  methods: {
    isToday(day) {
      return isSameDay(new Date(day.date), this.today);
    },
    isGreaterThanFinalDate(day) {
      if (!this.finalDate) {
        return false;
      }

      return isAfter(new Date(day.date), this.finalDate);
    },
    isLessThanInitialDate(day) {
      if (!this.initialDate) {
        return false;
      }

      return isBefore(new Date(day.date), this.initialDate);
    },
    isBreakPointDate(day) {
      return (
        (this.dateOne && day.date === this.dateOne.date) ||
        (this.dateTwo && day.date === this.dateTwo.date)
      );
    },
    isMiddleDate(day) {
      if (!this.dateOne || !this.dateTwo) {
        return false;
      }
      return (
        isAfter(new Date(day.date), new Date(this.dateOne.date)) &&
        isBefore(new Date(day.date), new Date(this.dateTwo.date))
      );
    },
    resetDates() {
      this.dateOne = null;
      this.dateTwo = null;
      this.$emit("select-date", null);
      this.$emit("start-date", null);
      this.$emit("end-date", null);
      this.dateConfirmation();
    },
    selectDate(day) {
      if (!day.date) {
        return;
      }

      if (this.mode === "single") {
        this.dateOne = day;
        this.dateTwo = null;
        this.$emit("select-date", this.dateOne);
        return;
      }
      if (
        this.dateOne &&
        isBefore(new Date(day.date), new Date(this.dateOne.date))
      ) {
        this.dateOne = null;
        this.dateTwo = null;
      }

      if (!this.dateOne || this.dateTwo) {
        this.dateOne = day;
        this.dateTwo = null;
        this.$emit("select-date", this.dateOne);
        this.$emit("start-date", this.dateOne);
      } else {
        this.dateTwo = day;
        this.$emit("select-date", this.dateTwo);
        this.$emit("end-date", this.dateTwo);
      }
    },
    dateConfirmation() {
      this.$emit("date-confirmation", {
        dateOne: this.dateOne,
        dateTwo: this.dateTwo,
      });
    },
    mountMonth(month) {
      return {
        name: this.monthNames[getMonth(month)],
        year: format(month, "yyyy"),
        key: format(month, "MM-dd-yyyy"),
        start: month,
        weeks: this.weeks_(month),
      };
    },
    nextMonth() {
      const month = startOfMonth(this.currentDate);
      this.currentDate = addMonths(month, 1);
    },
    previousMonth() {
      const month = startOfMonth(this.currentDate);
      this.currentDate = subMonths(month, 1);
    },
    createDayFromDate(raw) {
      if (!raw) {
        return null;
      }
      const isoDate = new Date(raw);
      const day = format(isoDate, "d");
      const label = ("00" + day).slice(-2);
      const date = format(isoDate, "MM-dd-yyyy");

      return {
        day,
        type: "day",
        label,
        date,
      };
    },
    weeks_(date) {
      const dayOne = startOfMonth(date);
      const firstWeekDay = parseInt(format(dayOne, "e"));
      const daysInMonth = getDaysInMonth(dayOne);

      let weeks = [];
      let week = [];

      for (let i = 1; i < firstWeekDay; i++) {
        week = [...week, { day: 0, type: "left" }];
      }

      for (let day = 1; day <= daysInMonth; day++) {
        const label = ("00" + day).slice(-2);
        week = [
          ...week,
          {
            day,
            type: "day",
            label,
            date:
              `${format(dayOne, "MM")}-` + label + `-${format(dayOne, "yyyy")}`,
            sql:
              `${format(dayOne, "yyyy")}-` + `${format(dayOne, "MM")}-` + label,
          },
        ];

        if (week.length === 7 || day === daysInMonth) {
          const faults = 7 - week.length;
          for (let i = 0; i < faults; i++) {
            week = [...week, { day: 0, type: "right" }];
          }
          weeks = [...weeks, week];
          week = [];
        }
      }

      return weeks;
    },
    monthInput(e) {
      this.currentDate = setYear(this.currentDate, e.target.value);
    }
  },
};
</script>

<style scoped lang="stylus">
.select-year
  width 72px
  min-height 0
</style>
